import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, ExternalLink, Pencil, Plus, Receipt, Search, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { CANALI, fmtDate, fmtEUR, nowPeriod, ymStr } from "@/lib/format";
import { ChannelBadge, ConfirmDialog, EmptyState, Loading, PageHeader, PeriodSelect, SimpleSelect, TipoBadge, btnGhost, btnPrimary, inputCls } from "@/components/app/common";
import { MovimentoDialog } from "@/components/app/MovimentoDialog";

const PAGE_SIZE = 25;

const Pager = ({ page, pages, total, onChange }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-[#756B64]" data-testid="movimenti-pagination">
    <span data-testid="pagination-info">Pagina {page} di {pages} · {total} movimenti</span>
    <div className="flex gap-2">
      <Button variant="outline" className={`${btnGhost} h-9`} disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4 mr-1" />Precedente</Button>
      <Button variant="outline" className={`${btnGhost} h-9`} disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="pagination-next">Successiva<ChevronRight className="h-4 w-4 ml-1" /></Button>
    </div>
  </div>
);

const Stat = ({ label, value, testId, color }) => (
  <div className="card-soft px-5 py-4" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold">{label}</div>
    <div className={`text-xl font-semibold tabular mt-1 ${color || ""}`}>{value}</div>
  </div>
);

export default function Movimenti() {
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState(nowPeriod());
  const [canale, setCanale] = useState("all");
  const [tipo, setTipo] = useState("all");
  const [categoria, setCategoria] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [categorie, setCategorie] = useState([]);
  const [edit, setEdit] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);
  const [page, setPage] = useState(1);
  const { isAdmin } = useAuth();

  const filterParams = useCallback(() => {
    const p = { ...(period.mese ? { mese: ymStr(period.anno, period.mese) } : {}) };
    if (canale !== "all") p.canale = canale;
    if (tipo !== "all") p.tipo = tipo;
    if (categoria !== "all") p.categoria = categoria;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [period, canale, tipo, categoria, q]);

  useEffect(() => { setPage(1); }, [filterParams]);

  const load = useCallback(() => {
    api.get("/movimenti", { params: { ...filterParams(), page, page_size: PAGE_SIZE } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [filterParams, page]);

  const exportXlsx = async () => {
    try { await downloadFile("/export/movimenti", filterParams(), "movimenti.xlsx"); toast.success("Download avviato"); }
    catch (e) { toast.error(e.message); }
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/categorie").then((r) => setCategorie(r.data.filter((c) => c.tipo !== "linea"))); }, []);
  useEffect(() => {
    if (params.get("nuovo")) { setEdit({ open: true, item: null }); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const remove = async () => {
    try { await api.delete(`/movimenti/${del.id}`); toast.success("Movimento eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  const catOpts = [{ value: "all", label: "Tutte le categorie" }, ...[...new Set(categorie.filter((c) => tipo === "all" || c.tipo === tipo).map((c) => c.nome))].map((n) => ({ value: n, label: n }))];

  return (
    <div data-testid="movimenti-page">
      <PageHeader overline="Prima nota" title="Movimenti" subtitle="Tutti gli incassi e i costi. L'IVA e il totale vengono calcolati automaticamente.">
        {isAdmin && <Button variant="outline" className={btnGhost} onClick={exportXlsx} data-testid="export-movimenti-button"><Download className="h-4 w-4 mr-1" />Esporta Excel</Button>}
        <Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })} data-testid="add-movimento-button"><Plus className="h-4 w-4 mr-1" />Nuovo movimento</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="movimenti-filters">
        <PeriodSelect anno={period.anno} mese={period.mese} onChange={setPeriod} allowYear prefix="filter" />
        <SimpleSelect testId="filter-channel-select" className="w-40" value={canale} onChange={setCanale} options={[{ value: "all", label: "Tutti i canali" }, ...CANALI.map((c) => ({ value: c, label: c }))]} />
        <SimpleSelect testId="filter-type-select" className="w-36" value={tipo} onChange={(v) => { setTipo(v); setCategoria("all"); }} options={[{ value: "all", label: "Incassi e costi" }, { value: "incasso", label: "Solo incassi" }, { value: "costo", label: "Solo costi" }]} />
        <SimpleSelect testId="filter-category-select" className="w-52" value={categoria} onChange={setCategoria} options={catOpts} />
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 absolute left-3 top-3 text-[#756B64]" />
          <Input data-testid="filter-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca descrizione…" className={`${inputCls} pl-9`} />
        </div>
      </div>

      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-5">
            <Stat testId="totale-incassi" label="Incassi" value={fmtEUR(data.totali.incassi)} color="text-[#4E6656]" />
            <Stat testId="totale-costi" label="Costi" value={fmtEUR(data.totali.costi)} color="text-[#A2513C]" />
            <Stat testId="totale-n" label="N. movimenti" value={data.totali.n} />
          </div>
          {!data.items.length ? (
            <EmptyState icon={Receipt} title="Nessun movimento trovato" text="Prova a cambiare i filtri o registra il primo movimento del periodo." action={<Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })} data-testid="empty-add-movimento">Crea nuovo movimento</Button>} />
          ) : (
            <>
              <MovimentiTable items={data.items} onEdit={(m) => setEdit({ open: true, item: m })} onDelete={setDel} />
              <Pager page={data.page} pages={data.pages} total={data.totali.n} onChange={setPage} />
            </>
          )}
        </>
      )}

      <MovimentoDialog open={edit.open} movimento={edit.item} categorie={categorie} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare il movimento?" text={del ? `${fmtDate(del.data)} · ${del.descrizione || del.categoria} · ${fmtEUR(del.imponibile)}` : ""} onConfirm={remove} />
    </div>
  );
}

const linkOf = (m) => (m.spesa_brand_id ? { to: "/spese-brand", label: "Spese brand" } : m.fattura_id ? { to: `/fatture/${m.fattura_id}`, label: "Fattura" } : m.uscita_id ? { to: "/uscite-merce", label: "Uscita" } : null);

function MovimentiTable({ items, onEdit, onDelete }) {
  return (
    <div className="card-soft overflow-x-auto" data-testid="movimenti-table">
      <table className="w-full text-sm min-w-[880px]">
        <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
          <tr>
            {["Data", "Tipo", "Canale", "Categoria", "Descrizione"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}
            {["Imponibile", "IVA", "Totale"].map((h) => <th key={h} className="text-right font-semibold px-4 py-3">{h}</th>)}
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-[#EAE3DC]">
          {items.map((m, i) => (
            <tr key={m.id} data-testid={`movimento-table-row-${i}`} className="hover:bg-[#FAF7F5] transition-colors">
              <td className="px-4 py-3 tabular whitespace-nowrap">{fmtDate(m.data)}</td>
              <td className="px-4 py-3"><TipoBadge tipo={m.tipo} ricorrente={!!m.ricorrente_id} /></td>
              <td className="px-4 py-3"><ChannelBadge canale={m.canale} /></td>
              <td className="px-4 py-3 text-[#5E4A42]">{m.categoria}</td>
              <td className="px-4 py-3 max-w-[240px] truncate" title={m.descrizione}>{m.descrizione || "—"}</td>
              <td className={`px-4 py-3 text-right tabular font-medium whitespace-nowrap ${m.imponibile < 0 ? "text-[#B4553D]" : ""}`}>{fmtEUR(m.imponibile)}</td>
              <td className="px-4 py-3 text-right tabular text-[#756B64] whitespace-nowrap">{fmtEUR(m.iva)} <span className="text-[10px]">({m.aliquota_iva}%)</span></td>
              <td className="px-4 py-3 text-right tabular font-semibold whitespace-nowrap">{fmtEUR(m.totale)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                {linkOf(m) ? (
                  <Link to={linkOf(m).to} data-testid={`linked-movimento-${i}`} title={`Movimento collegato: modifica da ${linkOf(m).label}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#8C6D62] hover:underline"><ExternalLink className="h-3.5 w-3.5" />Modifica da {linkOf(m).label}</Link>
                ) : <>
                <button data-testid={`edit-movimento-${i}`} onClick={() => onEdit(m)} className="p-2 rounded-lg hover:bg-[#F4EFEA] text-[#756B64]"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-movimento-${i}`} onClick={() => onDelete(m)} className="p-2 rounded-lg hover:bg-[#E07A5F]/10 text-[#B4553D]"><Trash2 className="h-4 w-4" /></button>
                </>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
